

import { useState } from "react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isAxiosError } from "axios";
import { toast } from "sonner";

/**
 * Creates a QueryClient with production-grade defaults.
 *
 * staleTime vs gcTime
 *   staleTime — how long before data is considered stale and a background
 *               refetch is scheduled on the next observer mount.
 *   gcTime    — how long unused (no observer) query data stays in the cache
 *               before being garbage-collected.
 *
 * retry
 *   Queries: 2 retries with exponential backoff, capped at 30 s.
 *   Mutations: 0 — mutations are not idempotent; retrying a write
 *              automatically can cause duplicate side-effects.
 *
 * QueryCache.onError
 *   Surfaces unexpected server errors (5xx) to the user via a toast.
 *   4xx responses are intentional (auth redirect, permission denied,
 *   not-found) and are handled by the Axios interceptors or inline
 *   component state; they are deliberately excluded here.
 */
function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError(error) {
        if (!isAxiosError(error)) return;
        const status = error.response?.status;
        // Only surface genuine server-side failures to the user.
        if (status !== undefined && status >= 500) {
          toast.error("Server error — please try again or contact support.");
        }
      },
    }),

    defaultOptions: {
      queries: {
        // 0 — data is always stale; every page mount triggers a background
        // refetch so users always see the latest state after any mutation.
        staleTime: 0,

        // 5 min — keep unused cache entries alive so back-navigation shows
        // something instantly while the background refetch completes.
        gcTime: 5 * 60_000,

        // Retry up to twice with exponential backoff (1 s → 2 s → 4 s, max 30 s).
        retry: 2,
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 30_000),

        // Refetch when the user returns to the tab — catches changes made in
        // other tabs or by other users while this tab was in the background.
        refetchOnWindowFocus: true,
      },

      mutations: {
        // Never auto-retry mutations: writes are not guaranteed to be idempotent,
        // so a silent retry could create duplicate records or apply a state
        // transition twice.
        retry: 0,
      },
    },
  });
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  // useState ensures each browser session gets one shared client instance
  // while each SSR request on the server gets its own, preventing
  // cross-request data leaking between users.
  const [queryClient] = useState(makeQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {import.meta.env.MODE !== "production" && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
